import type { AssistantChatMessage } from "@nevisign/reader-core";
import type { ReaderTokenUsage } from "./readerTools";

export type ReaderChatMessage = AssistantChatMessage & {
  usage?: ReaderTokenUsage;
};

export type FileChatThread = {
  id: string;
  fileKey: string;
  fileName: string;
  /** Device content:// or file:// URI for reopen */
  fileUri?: string;
  /** Local path hint when available */
  filePath?: string;
  mimeType?: string;
  byteLength?: number;
  /** Linked Nevisign cloud document id (studio chat sync). */
  nevisignDocumentId?: string;
  updatedAt: string;
  messages: ReaderChatMessage[];
};

const INDEX_KEY = "nr-chat-index";
const threadKey = (id: string) => `nr-chat:${id}`;

function hashName(name: string): string {
  let h = 2166136261;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

export function fileChatKey(fileName: string, byteLength?: number): string {
  const size = byteLength != null ? `:${byteLength}` : "";
  return `${hashName(fileName)}${size}:${fileName}`;
}

function loadIndex(): string[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const ids = JSON.parse(raw) as string[];
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

function saveIndex(ids: string[]): void {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(ids.slice(0, 40)));
  } catch {
    /* ignore */
  }
}

export function loadChatThread(id: string): FileChatThread | null {
  try {
    const raw = localStorage.getItem(threadKey(id));
    if (!raw) return null;
    return JSON.parse(raw) as FileChatThread;
  } catch {
    return null;
  }
}

export function saveChatThread(thread: FileChatThread): void {
  try {
    localStorage.setItem(threadKey(thread.id), JSON.stringify(thread));
    const ids = loadIndex().filter((x) => x !== thread.id);
    ids.unshift(thread.id);
    saveIndex(ids);
  } catch {
    /* ignore */
  }
}

export function listChatThreads(): FileChatThread[] {
  return loadIndex()
    .map((id) => loadChatThread(id))
    .filter((t): t is FileChatThread => Boolean(t))
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export type OpenFileMeta = {
  fileUri?: string;
  filePath?: string;
  mimeType?: string;
  byteLength?: number;
  nevisignDocumentId?: string;
};

export function getOrCreateChatForFile(
  fileName: string,
  byteLength?: number,
  meta?: OpenFileMeta
): FileChatThread {
  const fileKey = fileChatKey(fileName, byteLength);
  const existing = listChatThreads().find((t) => t.fileKey === fileKey);
  if (existing) {
    if (
      meta?.fileUri ||
      meta?.filePath ||
      meta?.mimeType ||
      meta?.nevisignDocumentId
    ) {
      const next: FileChatThread = {
        ...existing,
        fileUri: meta.fileUri ?? existing.fileUri,
        filePath: meta.filePath ?? existing.filePath,
        mimeType: meta.mimeType ?? existing.mimeType,
        nevisignDocumentId:
          meta.nevisignDocumentId ?? existing.nevisignDocumentId,
        byteLength: byteLength ?? existing.byteLength,
        updatedAt: new Date().toISOString(),
      };
      saveChatThread(next);
      return next;
    }
    return existing;
  }

  const thread: FileChatThread = {
    id: `chat-${crypto.randomUUID().slice(0, 10)}`,
    fileKey,
    fileName,
    fileUri: meta?.fileUri,
    filePath: meta?.filePath,
    mimeType: meta?.mimeType,
    nevisignDocumentId: meta?.nevisignDocumentId,
    byteLength,
    updatedAt: new Date().toISOString(),
    messages: [
      {
        role: "assistant",
        content:
          "سلام! من دستیار نویساین هستم. دربارهٔ این سند بپرسید یا درخواست بازبینی کنید.",
      },
    ],
  };
  saveChatThread(thread);
  return thread;
}

export function updateChatMessages(
  id: string,
  messages: ReaderChatMessage[]
): FileChatThread | null {
  const thread = loadChatThread(id);
  if (!thread) return null;
  const next = {
    ...thread,
    messages,
    updatedAt: new Date().toISOString(),
  };
  saveChatThread(next);
  return next;
}

export function updateChatFileMeta(
  id: string,
  meta: OpenFileMeta
): FileChatThread | null {
  const thread = loadChatThread(id);
  if (!thread) return null;
  const next: FileChatThread = {
    ...thread,
    fileUri: meta.fileUri ?? thread.fileUri,
    filePath: meta.filePath ?? thread.filePath,
    mimeType: meta.mimeType ?? thread.mimeType,
    nevisignDocumentId: meta.nevisignDocumentId ?? thread.nevisignDocumentId,
    byteLength: meta.byteLength ?? thread.byteLength,
    updatedAt: new Date().toISOString(),
  };
  saveChatThread(next);
  return next;
}
